import React from 'react';
import { EvalResultPayload } from './interfaces';
import './theme.css';

interface EvalScorecardProps {
  /** The run's final evaluation metrics, once the eval_result event arrives. Null before it does. */
  result: EvalResultPayload | null;
}

const RING_RADIUS = 26;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * Eval scorecard matching the approved design mockup
 * (smartwater-mockup-live.html): a dark scorecard card with one circular
 * progress ring per metric. Renders a run's final evaluation metrics (e.g.
 * NFL's r2/rmse/mae/mape) once the `eval_result` event arrives. Renders
 * nothing fabricated while waiting -- an explicit "not yet available"
 * state, per coding-standards.md's over-helpfulness guard.
 *
 * The mockup's rings show a fill proportional to "how good" each metric
 * is, but that requires a per-metric target/scale (e.g. R2's natural range
 * is 0-1, RMSE's is unbounded and dataset-dependent) that the backend does
 * not supply today. Rather than fabricate an arbitrary scale, every ring
 * renders fully filled (a static visual accent matching the mockup's
 * style) and the real value is shown as text -- the metric's actual
 * number, not a guessed-at progress percentage.
 */
export function EvalScorecard({ result }: EvalScorecardProps) {
  if (!result) {
    return (
      <div className="scorecard">
        <div className="title">Evaluation results not yet available for this run.</div>
      </div>
    );
  }

  return (
    <div className="scorecard">
      <div className="title">Model quality scorecard</div>
      <div className="ring-row">
        {Object.entries(result.metrics).map(([metricName, value]) => (
          <div className="ring" key={metricName}>
            <svg viewBox="0 0 64 64">
              <circle className="ring-bg" cx="32" cy="32" r={RING_RADIUS} />
              <circle
                className="ring-fg good"
                cx="32"
                cy="32"
                r={RING_RADIUS}
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={0}
              />
            </svg>
            <div className="ring-num">{value.toFixed(2)}</div>
            <div className="ring-label">{metricName.toUpperCase()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
